import { WorkerPositionEntityListResp } from '@api/responseModels/worker/workerPositionEntityListResponse';
import { WorkerPositionFreeSlotsListResponse } from '@api/responseModels/worker/workerPositionFreeSlotsListResponse';
import { WorkerPositionListResp } from '@api/responseModels/worker/workerPositionListResponse';
import { WorkerPositionTransactionsResp } from '@api/responseModels/worker/workerPositionTransactionsResponse';

import { TWorkerPositionUpdatePayload } from '../../types/worker';
import { CRMAPIBase } from '../crmApiBase';
import { RequestResult } from '../responseModels/requestResult';
import { WorkerPositionResp } from '../responseModels/worker/workerPositionResponse';

class WorkerPositionGroup extends CRMAPIBase {
    /**
     * Добавление рабочего места
     * @param crmId ID CRM
     * @param workerPosition Рабочее место
     * @returns Результат запроса
     */
    async saveWorkerPosition(
        crmId: number,
        workerPosition: Omit<TWorkerPositionUpdatePayload, 'id'>
    ): Promise<RequestResult<WorkerPositionResp>> {
        const result = await this.post<WorkerPositionResp>('/worker-positions', {
            crm_id: crmId,
            ...workerPosition,
        });
        return result;
    }

    /**
     * Получение рабочего места
     * @param id ID сотрудника
     * @param crmId ID CRM
     * @returns Результат запроса
     */
    async getWorkerPosition(id: number, crmId: number): Promise<RequestResult<WorkerPositionResp>> {
        const result = await this.get<WorkerPositionResp>(`/worker-positions/${id}`, {
            crm_id: crmId,
        });
        return result;
    }

    /**
     * Обновление рабочего места
     * @param crmId ID CRM
     * @param workerPosition Рабочее место
     * @returns Результат запроса
     */
    async updateWorkerPosition(
        crmId: number,
        workerPosition: TWorkerPositionUpdatePayload
    ): Promise<RequestResult<WorkerPositionResp>> {
        const result = await this.put<WorkerPositionResp>(
            `/worker-positions/${workerPosition.id}`,
            {
                crm_id: crmId,
                ...workerPosition,
            }
        );
        return result;
    }

    /**
     * Удаление рабочего места
     * @param id ID рабочего места
     * @param crmId ID CRM
     * @returns Результат запроса
     */
    async removeWorkerPosition(
        id: number,
        crmId: number
    ): Promise<RequestResult<WorkerPositionResp>> {
        const result = await this.delete<WorkerPositionResp>(`/worker-positions/${id}`, {
            crm_id: crmId,
        });
        return result;
    }

    /**
     * Восстановление рабочего места
     * @param id ID рабочего места
     * @param crmId ID CRM
     * @returns Результат запроса
     */
    async restoreWorkerPosition(
        id: number,
        crmId: number
    ): Promise<RequestResult<WorkerPositionResp>> {
        const result = await this.post<WorkerPositionResp>(`/worker-positions/${id}/restore`, {
            crm_id: crmId,
        });
        return result;
    }

    /**
     * Получение списка рабочих мест
     * @param crmId ID CRM
     * @returns Результат запроса
     */
    async getWorkerPositionList(params: any): Promise<RequestResult<WorkerPositionListResp>> {
        const result = await this.get<WorkerPositionListResp>(`/worker-positions`, params);
        return result;
    }

    async getWorkerPositionEntityList<T>(
        params,
        id
    ): Promise<RequestResult<WorkerPositionEntityListResp<T>>> {
        const result = await this.get<WorkerPositionEntityListResp<T>>(
            `/worker-positions/${id}/related-data`,
            {
                ...params,
                is_permission: true,
            }
        );
        return result;
    }
    async getWorkerPositionFreeSlots(
        params: any
    ): Promise<RequestResult<WorkerPositionFreeSlotsListResponse>> {
        const result = await this.post<WorkerPositionFreeSlotsListResponse>(
            `/worker-positions/free-slots`,
            params
        );
        return result;
    }

    // Увольнение текущего пользователя с рабочего места
    async fireEmployee(
        workerPositionId: number,
        crmId: number
    ): Promise<RequestResult<WorkerPositionFreeSlotsListResponse>> {
        const result = await this.post<null>(`/worker-positions/${workerPositionId}/fireEmployee`, {
            crm_id: crmId,
        });
        return result;
    }

    /**
     * Получение списка транзакций рабочего места
     * @param workerPositionId ID рабочего места
     * @param params { crm_id, page, per_page }
     * @returns Результат запроса
     */
    async getWorkerPositionTransactions(
        workerPositionId: number,
        params: { crm_id: number; page?: number; per_page?: number }
    ): Promise<RequestResult<WorkerPositionTransactionsResp>> {
        const result = await this.get<WorkerPositionTransactionsResp>(
            `/worker-positions/${workerPositionId}/transactions`,
            params
        );
        return result;
    }

    async applyWorkerPositionStatusReason({
        user_id,
        crm_id,
        reason_key,
        comment,
        prev_status_id,
        order_id,
        status_id,
        ...rest
    }: {
        user_id: number;
        crm_id: number;
        reason_key: string;
        comment: string;
        prev_status_id?: number;
        order_id?: number;
        status_id?: number;
    }): Promise<RequestResult<null>> {
        const result = await this.post<null>(`worker-positions/${user_id}/apply-reason`, {
            crm_id,
            reason_key,
            comment,
            prev_status_id,
            order_id,
            status_id,
            ...rest,
        });
        return result;
    }
}

export { WorkerPositionGroup };
